<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <router-link to="/tenants/CertificateList" class="small d-inline-block mb-2">&larr; Licences and certificates</router-link>
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">{{ obj.id ? obj.name || "Certificate" : "New certificate" }}</h1>
            <p class="text-muted mb-0">Add the expiry and reminder dates so you renew on time</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton color="primary" :disabled="saving" @click="submit">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Add certificate" }}
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CRow>
      <CCol lg="8">
        <CCard>
          <CCardHeader><strong>Details</strong></CCardHeader>
          <CCardBody>
            <CForm @submit.prevent="submit">
              <CInput
                label="Name *"
                horizontal
                v-model="obj.name"
                placeholder="For example, Business premises licence"
                :is-valid="nameError ? false : null"
                :invalid-feedback="nameError"
              />
              <CInput
                label="Type"
                horizontal
                v-model="obj.documentType"
                placeholder="For example, Licence"
              />
              <CInput
                label="Certificate no."
                horizontal
                v-model="obj.certificateNumber"
              />
              <CInput
                horizontal
                label="Issued *"
                type="date"
                :value="computeIssuedDate"
                @change="setIssuedDate"
              />
              <CInput
                horizontal
                label="Expires"
                type="date"
                :value="computeExpiryDate"
                @change="setExpiryDate"
              />
              <CInput
                horizontal
                label="Remind me on"
                type="date"
                :value="computeReminderDate"
                @change="setReminderDate"
                description="We'll remind you on this date so you can renew in time."
              />
              <CTextarea
                horizontal
                label="Notes"
                v-model="obj.description"
                rows="4"
              />
              <CRow form class="form-group">
                <CCol tag="label" sm="3" class="col-form-label">Active</CCol>
                <CCol sm="9">
                  <CSwitch
                    class="mr-1"
                    color="primary"
                    :checked.sync="obj.isActive"
                    aria-label="Active"
                  />
                  <small class="form-text text-muted">Turn off for certificates you no longer need reminders for.</small>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol lg="4">
        <CCard>
          <CCardHeader><strong>Documents</strong></CCardHeader>
          <CCardBody>
            <p v-if="!computedCertificateDocuments.length" class="text-muted">
              No documents yet. Upload a scan or photo of the certificate.
            </p>
            <ul v-else class="list-unstyled mb-3">
              <li
                v-for="item in computedCertificateDocuments"
                :key="item.documentId"
                class="d-flex justify-content-between align-items-center border-bottom py-2"
              >
                <CLink target="_blank" :href="item.documentUrl" class="text-truncate mr-2">{{ item.documentName }}</CLink>
                <CButton
                  color="light"
                  :aria-label="`Remove ${item.documentName}`"
                  title="Remove"
                  @click="removeDocumentConfirmation(item)"
                >
                  <CIcon name="cil-trash" />
                </CButton>
              </li>
            </ul>
            <WidgetsUploadDocument @uploaded="uploaded" />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CModal
      title="Remove this document?"
      :show.sync="removeDocumentWarningModal"
    >
      <p class="mb-0">
        {{ bolDocumentToDelete ? bolDocumentToDelete.documentName : "The document" }} will be removed from this
        certificate when you save.
      </p>
      <template #footer>
        <CButton color="light" @click="removeDocumentWarningModal = false">Keep it</CButton>
        <CButton color="danger" @click="onRemoveDocumentConfirmation(null, null, true)">Remove document</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import CertificateApi from "@/lib/certificateApi";
import { asDateString } from "@/utils/datetime";
import WidgetsUploadDocument from "../widgets/WidgetsUploadDocument.vue";

export default {
  name: "Certificate",
  components: {
    WidgetsUploadDocument,
  },
  data: () => {
    return {
      bolDocumentToDelete: null,
      removeDocumentWarningModal: false,

      certificateDocuments: [],
      documentFieldList: [
        {
          key: "document_link",
          label: "Document",
          sorter: false,
          filter: false,
        },
        {
          key: "remove",
          label: "",
          sorter: false,
          filter: false,
        },
      ],
      uploadedFiles: [],
      issuedDate: Date(),
      expiryDate: Date(),
      reminderDate: Date(),
      organizationTypeList: [],
      saving: false,
      nameError: "",
      obj: {},
      submitted: false,
      api: new CertificateApi(),
    };
  },
  mounted() {
    var self = this;
    this.initializeDefaultDate();
    self.resetObj();
  },
  computed: {
    computedCertificateDocuments() {
      return this.certificateDocuments.map((item) => {
        return {
          ...item,
          documentName: this.getCollectionDocumentName(item),
          documentUrl: this.getCollectionDocumentUrl(item),
        };
      });
    },

    computeIssuedDate() {
      return asDateString(this.issuedDate);
    },
    computeExpiryDate() {
      return asDateString(this.expiryDate);
    },
    computeReminderDate() {
      return asDateString(this.reminderDate);
    },
    documentUrl() {
      return apiUrl + "documents/file/" + this.obj.documentId;
    },
  },
  methods: {
    uploaded(data) {
      if (!data || !data.uploadedFiles || !data.uploadedFiles.length) return;
      let newDocument = {
        documentId: data.uploadedFiles[0].id,
        document: {
          id: data.uploadedFiles[0].id,
          fileName: data.uploadedFiles[0].fileName,
        },
        // documentName: data.uploadedFiles[0].fileName,
        // documentUrl: apiUrl + data.uploadedFiles[0].url,
      };

      this.certificateDocuments.push(newDocument);
   
    },
    removeDocumentConfirmation(item) {
      var self = this;
      self.bolDocumentToDelete = item;
      self.removeDocumentWarningModal = true;
    },
    // onRemoveDocumentConfirmation(status, evt, accept) {
    //   if (accept) {
    //   }
    // },
    onRemoveDocumentConfirmation(status, evt, accept) {
  if (accept && this.bolDocumentToDelete) {
    // Find the index of the document to remove
    const index = this.certificateDocuments.findIndex(
      (doc) => doc.documentId === this.bolDocumentToDelete.documentId
    );

    if (index !== -1) {
      // Remove document from UI list
      this.certificateDocuments.splice(index, 1);

      // Call API to remove from backend
      // this.api
      //   .removeBolDocument(this.obj.id, this.bolDocumentToDelete.documentId)
      //   .then(() => {
      //     this.toast("Success", "Document removed successfully.", "success");
      //   })
      //   .catch(({ data }) => {
      //     this.toast("Error", "Failed to remove document.", "danger");
      //   });
    }

    // Reset modal and reference to deleted document
    this.bolDocumentToDelete = null;
    this.removeDocumentWarningModal = false;
  }
},

    getCollectionDocumentName(item) {
      if (item.document == null) return "Unknown";
      if (item.document.fileName == null) return "Unknown";

      return item.document.fileName;
    },
    getCollectionDocumentUrl(item) {
      var self = this;
      return apiUrl + "documents/file/" + item.documentId;
    },

    cancel() {
      this.$router.push({ path: "/tenants/CertificateList" });
    },
    initializeDefaultDate() {
      const today = new Date();
      this.issuedDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0
      );
      this.expiryDate = this.reminderDate = this.issuedDate;
    },
    getDocumentUrl() {
      var self = this;
      return apiUrl + "documents/file/" + this.obj.documentId;
    },
    getDocumentName() {
      if (this.obj.document == null) return "Unknown";
      if (this.obj.document.fileName == null) return "Unknown";
      return this.obj.document.fileName;
    },
    setIssuedDate(e) {
      this.issuedDate = e; // ISO format assumes local time
    },
    setExpiryDate(e) {
      this.expiryDate = e; // ISO format assumes local time
    },
    setReminderDate(e) {
      this.reminderDate = e; // ISO format assumes local time
    },
    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result;
            this.issuedDate = self.obj.issuedDate;
            this.expiryDate = self.obj.expiryDate;
            this.reminderDate = self.obj.reminderDate;
            self.certificateDocuments = self.obj.certificateDocuments || [];
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't load the certificate. ${helper.getErrorMessage(data)}`, "danger");
          });
      } else {
        self.obj = self.getEmptyObj();
      }
    },
    onSubmit() {
      var self = this;
      if (!self.obj.name || !String(self.obj.name).trim()) {
        self.nameError = "Enter a name, for example Business premises licence.";
        return;
      }
      self.nameError = "";
      if (self.saving) return;
      self.saving = true;
      self.obj.issuedDate = self.issuedDate;
      self.obj.expiryDate = self.expiryDate;
      self.obj.reminderDate = self.reminderDate;
      self.obj.certificateDocuments = self.certificateDocuments;

      if (!self.obj.id) {
        this.api
          .create(self.obj)
          .then((response) => {
            self.toast("Success", "Certificate added", "success");
            self.$router.push({ path: "/tenants/certificateList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't add the certificate. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      } else {
        this.api
          .update(self.obj)
          .then((response) => {
            self.toast("Success", "Certificate saved", "success");
            self.$router.push({ path: "/tenants/certificateList" });
          })
          .catch(({ data }) => {
            self.toast("Error", `Couldn't save the certificate. ${helper.getErrorMessage(data)}`, "danger");
          })
          .finally(() => {
            self.saving = false;
          });
      }
    },
    onReset() {
      this.obj = {};
    },
    toast(header, message, color) {
      const kind = { danger: "error", success: "success", warning: "warning" }[color] || "info";
      this.$toast[kind](message);
    },

    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === "");
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      this.resetObj();
      this.submitted = false;
      this.$v.$reset();
    },
    getEmptyObj() {
      return {
        isActive: true,
        certificateDocuments: [],
        // code: "",
        // name: "",
        // decsription: "",
      };
    },
    submit() {
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>
